import { BOARD_SIZE, getId, DEV_MODE } from './constants';
import { EntityKind, Entities, matcher } from './state/entityKinds';
import { getXYindex } from './state/gameState';
import { getDevEmitter, dev_event } from '../devUI/devEvents';
import {
  getDefaultEntity,
  getDevApplicationState,
} from '../devUI/devApplicationGlobal';
import { DatabaseHandle } from './database/database';

// Use the data property on sprites.
class LevelEntityDeduper {
  private store: any;
  constructor(store?: any) {
    this.store = store || LevelEntityDeduper.getDefaultStore();
  }

  static getDefaultStore(): any {
    const store: any = {};
    for (const key of Object.keys(getDefaultEntity)) {
      store[key] = new Set();
    }
    return store;
  }

  addEntity(e: EntityKind, idx: number): boolean {
    if (e === EntityKind.PlayerBody && this.store[e].size > 0) {
      return false;
    }
    if (this.store[e].has(idx)) {
      return false;
    }
    this.store[e].add(idx);

    return true;
  }
  removeEntity(e: EntityKind, idx: number): boolean {
    if (!this.store[e].has(idx)) {
      return false;
    }
    this.store[e].delete(idx);

    return true;
  }
}

class EditorState {
  public painting: Entities | null;
  public selecting: boolean;
  constructor() {
    this.painting = {
      kind: EntityKind.StaticWall,
      x: -1,
      y: -1,
      id: getId(),
    };
    this.selecting = false;
  }
}

export default class EditScene extends Phaser.Scene {
  public initialLevelState: LevelEntityDeduper;
  public marker: Phaser.GameObjects.Graphics;
  public editorState: EditorState;
  public ignore_mouse: boolean;
  private entities: Entities[];

  public x_pos: number;
  public y_pos: number;

  constructor(config: any) {
    super(config);
    this.initialLevelState = new LevelEntityDeduper();
    this.x_pos = 0;
    this.y_pos = 0;
    this.editorState = new EditorState();
    this.ignore_mouse = true;
    this.entities = [];
  }

  init({ tilemapKey, entities }: { tilemapKey: string; entities: Entities[] }) {
    if (tilemapKey === undefined) {
    }
    this.entities = entities;
  }

  create() {
    this.marker = this.add.graphics();
    this.marker.lineStyle(1, 0x000000, 1);
    this.marker.strokeRect(0.5, 0.5, BOARD_SIZE - 1, BOARD_SIZE - 1);
    const onGameOut = () => {
      this.ignore_mouse = true;
      this.marker.setVisible(false);
    };
    const onGameOver = () => {
      this.ignore_mouse = false;
      this.marker.setVisible(true);
    };

    if (DEV_MODE) {
      const paintHandler = ({ entity }) => {
        this.editorState.painting = entity;
        this.editorState.selecting = false;
      };
      const eraserHandler = () => {
        this.editorState.painting = null;
        this.editorState.selecting = false;
      };
      const selectorHandler = () => {
        this.editorState.painting = null;
        this.editorState.selecting = true;
      };
      const saveEditor = (asFile: boolean) => {
        this.getAllEntitiesFromLevelAndSaveLevel(
          getDevApplicationState()?.currentLevelKey ||
            (() => {
              throw new Error('level has no name!');
            })(),
          asFile
        );
      };
      getDevEmitter()?.on(dev_event.Paint_pencil, paintHandler, this);
      getDevEmitter()?.on(dev_event.Paint_eraser, eraserHandler, this);
      getDevEmitter()?.on(dev_event.Paint_Select, selectorHandler, this);
      getDevEmitter()?.on(dev_event.Save_Editor_Level, saveEditor, this);

      this.events.once('destroy', () => {
        getDevEmitter()?.removeListener(
          dev_event.Paint_pencil,
          paintHandler,
          this
        );
        getDevEmitter()?.removeListener(
          dev_event.Paint_eraser,
          eraserHandler,
          this
        );
        getDevEmitter()?.removeListener(
          dev_event.Paint_Select,
          selectorHandler,
          this
        );
        getDevEmitter()?.removeListener(
          dev_event.Save_Editor_Level,
          saveEditor,
          this
        );
      });
    }

    this.input.on('gameout', onGameOut, this);
    this.input.on('gameover', onGameOver, this);
    this.events.once('destroy', () => {
      this.input.removeListener('gameout', onGameOut, this);
      this.input.removeListener('gameover', onGameOver, this);
    });
    this.cameras.main.setBackgroundColor(0x003000);

    console.log(this.entities);
    for (const e of this.entities) {
      this.placeEntity(e);
    }
  }

  // Places an interactive sprite on the stage.
  placeEntity(e_: Entities) {
    const e = { ...e_ };
    if (e.x === -1 || e.y === -1 || e.id === -1) {
      e.x = this.x_pos;
      e.y = this.y_pos;
      e.id = getId();
    }
    const e_kind = e.kind;
    const placementIdx = getXYindex(e.x, e.y);
    if (this.initialLevelState.addEntity(e_kind, placementIdx)) {
      const sprite = this.add.sprite(
        e.x * 8,
        e.y * 8,
        'atlas',
        matcher<string>({
          [EntityKind.StaticWall]: () => 'TileMap/Obj_StaticSolid',
          [EntityKind.Crate]: () => 'Crate/Crate_1',
          [EntityKind.Empty]: () => 'TileMap/2single_hole',
          [EntityKind.PlayerBullet]: () => {
            throw new Error('unimplemented!');
          },
          [EntityKind.PressurePlate]: () => 'Placeholders/temp_button_up1',
          [EntityKind.MeleeEnemy]: () => 'Player_triangle',
          [EntityKind.Door]: () => 'TileMap/Obj_Door',
          [EntityKind.PlayerGun]: () => {
            throw new Error('unimplemented!');
          },
          [EntityKind.PlayerBody]: (e) => `TileMap/Obj_Player_Up`,
          [EntityKind.TDamage]: () => {
            throw new Error('unimplemented!');
          },
          [EntityKind.WinLevel]: () => 'Teleporter/Teleporter1',
          [EntityKind.ExtendedBlock]: () => 'TileMap/Obj_Empty',
          [EntityKind.LazorToggle]: () => 'LazorToggle/LToggle_Right_Off',
        })(e)
      );
      sprite.setData('entity', e);
      if (e.kind === EntityKind.LazorToggle) {
        sprite.setDepth(1000);
      } else {
        sprite.setDepth(0);
      }

      sprite.setInteractive({ useHandCursor: false }).on(
        'pointerdown',
        function () {
          if (this.editorState.selecting) {
            getDevEmitter()?.emit(
              dev_event.Paint_Selected_Resp,
              sprite.getData('entity')
            );
            return;
          }
          if ((this as any).editorState.painting === null) {
            sprite.destroy();
            this.initialLevelState.removeEntity(e_kind, placementIdx);
          }
        },
        this
      );
    }
  }

  async getAllEntitiesFromLevelAndSaveLevel(
    levelName: string,
    asFile: boolean
  ) {
    const result = await DatabaseHandle.getLevel(levelName);
    const entities: Entities[] = [];
    for (const go of this.children.list) {
      if (go.type === 'Sprite') {
        const e = go.getData('entity');
        entities.push(e);
      }
    }
    await DatabaseHandle.setLevel(levelName, entities, result.displayName);
    if (asFile) {
      this.load.saveJSON(
        await DatabaseHandle.getLevel(levelName),
        `${levelName}.json`
      );
    }
  }

  update(time: number, delta: number) {
    const worldPoint: Phaser.Math.Vector2 = this.input.activePointer.positionToCamera(
      this.cameras.main
    ) as Phaser.Math.Vector2;

    this.x_pos = Phaser.Math.Clamp(Math.floor(worldPoint.x / 8), 0, 15);
    this.y_pos = Phaser.Math.Clamp(Math.floor(worldPoint.y / 8), 0, 15);

    this.marker.x = this.x_pos * 8;
    this.marker.y = this.y_pos * 8;

    if (this.input.manager.activePointer.isDown && !this.ignore_mouse) {
      if (this.editorState.painting) {
        this.placeEntity(this.editorState.painting);
      }
    }

    for (const go of this.children.list) {
      if (go.type === 'Sprite') {
        const e = go.getData('entity');
        if (e.kind === EntityKind.Crate) {
          if (e.reflects === '/') {
            go.setFrame('Crate/Mirror_diagonal1');
          } else if (e.reflects === '\\') {
            go.setFrame('Crate/Mirror_diagonal2');
          }
        }
      }
    }
  }
}
